import React, { useCallback, useState } from 'react';

import { useIntl } from 'react-intl';

import ActionButton, { ActionButtonSizes } from 'components/action-button';
import LanguageSelectorModal from 'components/language-selector-modal';
import StoreCardIcons from 'components/store-card-icons';
import { useStoreCard } from 'hooks/store-card';
import { useServiceModeStatus } from 'hooks/use-service-mode-status';
import { useLocale } from 'state/intl';
import { useRestaurantPosConnectivityStatus } from 'state/restaurant-connectivity-status';

import { messages } from './store-card-messages';
import { ActionContainer } from './styled';
import { ICardFooterProps } from './types';

const StoreCardFooter: React.FC<ICardFooterProps> = ({
  restaurant,
  isRestaurantSelectable,
  selectRestaurant,
  areStoreCardDiagnosticsEnabled = false,
  storeDiagnosticData,
}: ICardFooterProps) => {
  const { formatMessage } = useIntl();
  const { region } = useLocale();
  const { allServiceModesUnavailable } = useServiceModeStatus(restaurant);
  const {
    isRestaurantPosOnline,
    loading: isAvailabilityLoading,
  } = useRestaurantPosConnectivityStatus(restaurant?.number ?? '');
  const { enableOrdering } = useStoreCard(restaurant);

  const [showLanguageSelectorModal, setShowLanguageSelectorModal] = useState<boolean>(false);

  const orderOrSelectButtonText = formatMessage(
    enableOrdering ? messages.orderHere : messages.select
  );

  const handleModalDismiss = useCallback(() => {
    setShowLanguageSelectorModal(false);
  }, [setShowLanguageSelectorModal]);

  const onOrderOrSelectClick = () => {
    // TODO: Add converter from 2-letter to 3-letter country
    // To region hook instead of using startsWith
    const restaurantCountry = restaurant?.physicalAddress?.country || '';
    const isCorrectRegion = restaurantCountry.toUpperCase().startsWith(region);

    isCorrectRegion ? selectRestaurant() : setShowLanguageSelectorModal(true);
  };

  const modalRegionFilter = useCallback(
    (regionForFilter: string) => {
      return (restaurant?.physicalAddress?.country || '').toUpperCase().startsWith(regionForFilter);
    },
    [restaurant]
  );

  const hasCountryAvailable = !!(restaurant?.physicalAddress?.country || '');

  const isDisabled =
    !enableOrdering ||
    allServiceModesUnavailable ||
    !isRestaurantSelectable ||
    !hasCountryAvailable ||
    !isRestaurantPosOnline;

  return (
    <ActionContainer>
      <StoreCardIcons
        storeNumber={restaurant.number ?? ''}
        storeId={restaurant._id ?? ''}
        diagnosticsEnabled={areStoreCardDiagnosticsEnabled}
        storeDiagnosticData={storeDiagnosticData}
      />
      <ActionButton
        data-testid={enableOrdering ? 'order-here' : 'select-this-store'}
        disabled={isDisabled}
        onClick={onOrderOrSelectClick}
        size={ActionButtonSizes.SMALL}
        aria-label={formatMessage(
          enableOrdering ? messages.orderAtThisStore : messages.selectRestaurant
        )}
        isLoading={isAvailabilityLoading}
      >
        {orderOrSelectButtonText}
      </ActionButton>
      {showLanguageSelectorModal && (
        <LanguageSelectorModal
          redirectToCurrentLocation
          heading={formatMessage(messages.changeLanguage)}
          regionFilter={modalRegionFilter}
          onModalDismiss={handleModalDismiss}
          primaryText={formatMessage(messages.changeRegionOnOrder)}
          secondaryText={formatMessage(messages.changeRegionConfirmation)}
          disclaimer={formatMessage(messages.changeRegionDisclaimer)}
        />
      )}
    </ActionContainer>
  );
};

export default StoreCardFooter;
